import {
  CheckCircle,
  Science,
  TrendingUp,
  Warning,
  Info,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { AB_TEST_MIN_SAMPLE_SIZE } from "../../../constants";

interface VariantMetrics {
  variantId: string;
  name: string;
  weight: number;
  isControl: boolean;
  isActive: boolean;
  executions: number;
  opens: number;
  clicks: number;
  replies: number;
  bookings: number;
  openRate: number;
  clickRate: number;
  replyRate: number;
  bookingRate: number;
  confidenceIntervals?: {
    openRate?: [number, number];
    clickRate?: [number, number];
    replyRate?: [number, number];
    bookingRate?: [number, number];
  };
}

interface StatisticalAnalysis {
  hasSignificantWinner: boolean;
  winningVariantId?: string;
  sampleSizeSufficient: boolean;
  minSampleSize: number;
  currentMinExecutions: number;
  recommendation?: string;
  metricWinners?: {
    openRate?: string;
    clickRate?: string;
    replyRate?: string;
    bookingRate?: string;
  };
  comparisons?: {
    [metricName: string]: {
      challengerVariantId: string;
      challengerName: string;
      controlVariantId: string;
      controlName: string;
      challengerRate: number;
      controlRate: number;
      pValue: number;
      significant: boolean;
      lift: number;
    }[];
  };
}

interface ABTestMetricsCardProps {
  variantMetrics: VariantMetrics[];
  statisticalAnalysis: StatisticalAnalysis;
}

const ABTestMetricsCard: React.FC<ABTestMetricsCardProps> = ({
  variantMetrics,
  statisticalAnalysis,
}) => {
  if (!variantMetrics || variantMetrics.length === 0) {
    return (
      <Alert severity="info" icon={<Science />}>
        No A/B test data available yet. Variants need to be executed to see metrics.
      </Alert>
    );
  }

  const renderRateWithCI = (
    rate: number,
    ci?: [number, number],
    isWinner?: boolean
  ) => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography
          variant="body2"
          fontWeight={isWinner ? 600 : 400}
          color={isWinner ? "primary" : "inherit"}
        >
          {rate.toFixed(1)}%
        </Typography>
        {ci && (
          <Tooltip title={`95% CI: ${ci[0].toFixed(1)}% - ${ci[1].toFixed(1)}%`}>
            <Typography variant="caption" color="text.secondary">
              ({ci[0].toFixed(0)}-{ci[1].toFixed(0)})
            </Typography>
          </Tooltip>
        )}
        {isWinner && (
          <CheckCircle color="primary" sx={{ fontSize: 14 }} />
        )}
      </Box>
    );
  };

  const isWinnerForMetric = (variantId: string, metricName: string) => {
    return statisticalAnalysis.metricWinners?.[metricName as keyof typeof statisticalAnalysis.metricWinners] === variantId;
  };

  const winnerVariant = variantMetrics.find(
    (v) => v.variantId === statisticalAnalysis.winningVariantId
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Science color="primary" />
          <Typography variant="h6">A/B Test Results</Typography>
        </Box>

        {/* Status Alert */}
        {!statisticalAnalysis.sampleSizeSufficient ? (
          <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Collecting data...</strong> Need at least{" "}
              {AB_TEST_MIN_SAMPLE_SIZE} executions per variant for statistical
              significance. Current minimum: {statisticalAnalysis.currentMinExecutions}.
            </Typography>
          </Alert>
        ) : statisticalAnalysis.hasSignificantWinner && winnerVariant ? (
          <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Winner detected!</strong> "{winnerVariant.name}" is
              performing significantly better.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              {statisticalAnalysis.recommendation ||
                "No clear winner yet. Continue testing for more conclusive results."}
            </Typography>
          </Alert>
        )}

        {/* Metrics Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Variant</TableCell>
                <TableCell align="center">Weight</TableCell>
                <TableCell align="center">Sent</TableCell>
                <TableCell align="center">Open Rate</TableCell>
                <TableCell align="center">Click Rate</TableCell>
                <TableCell align="center">Reply Rate</TableCell>
                <TableCell align="center">Booking Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variantMetrics.map((variant) => (
                <TableRow
                  key={variant.variantId}
                  sx={{
                    backgroundColor:
                      variant.variantId === statisticalAnalysis.winningVariantId
                        ? "action.selected"
                        : "inherit",
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {variant.name}
                      </Typography>
                      {variant.isControl && (
                        <Chip
                          label="Control"
                          size="small"
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      )}
                      {variant.variantId === statisticalAnalysis.winningVariantId && (
                        <Chip
                          label="Winner"
                          size="small"
                          color="primary"
                          icon={<TrendingUp sx={{ fontSize: 14 }} />}
                          sx={{ height: 20 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{variant.weight}%</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{variant.executions}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {renderRateWithCI(
                      variant.openRate,
                      variant.confidenceIntervals?.openRate,
                      isWinnerForMetric(variant.variantId, "openRate")
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {renderRateWithCI(
                      variant.clickRate,
                      variant.confidenceIntervals?.clickRate,
                      isWinnerForMetric(variant.variantId, "clickRate")
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {renderRateWithCI(
                      variant.replyRate,
                      variant.confidenceIntervals?.replyRate,
                      isWinnerForMetric(variant.variantId, "replyRate")
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {renderRateWithCI(
                      variant.bookingRate,
                      variant.confidenceIntervals?.bookingRate,
                      isWinnerForMetric(variant.variantId, "bookingRate")
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Statistical Comparisons */}
        {statisticalAnalysis.comparisons &&
          Object.keys(statisticalAnalysis.comparisons).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Statistical Comparisons
              </Typography>
              {Object.entries(statisticalAnalysis.comparisons).map(
                ([metricName, comparisons]) => (
                  <Box key={metricName} sx={{ mb: 1 }}>
                    {comparisons.map((comp, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          backgroundColor: comp.significant
                            ? "action.hover"
                            : "transparent",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption" sx={{ minWidth: 80 }}>
                          {metricName
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())}
                          :
                        </Typography>
                        <Typography variant="caption">
                          {comp.challengerName} vs {comp.controlName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={comp.lift > 0 ? "success.main" : "error.main"}
                        >
                          {comp.lift > 0 ? "+" : ""}
                          {comp.lift.toFixed(1)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          (p={comp.pValue.toFixed(3)})
                        </Typography>
                        {comp.significant && (
                          <Chip
                            label="Significant"
                            size="small"
                            color="primary"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )
              )}
            </Box>
          )}
      </CardContent>
    </Card>
  );
};

export default ABTestMetricsCard;
